import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { IMAGES, BRAND } from "@/lib/brand";
import { EnquiryForm } from "@/components/EnquiryForm";
import { TrustBand } from "@/components/TrustBand";
import { Reveal } from "@/components/Reveal";
import { Wifi, Utensils, Mountain, ShieldCheck, Wind, Coffee, ArrowUpRight } from "lucide-react";
const rooms = [
  { no: "01", name: "Premium Couple",       img: IMAGES.roomBed,     feature: "For two travellers",   rate: 3450, desc: "A serene retreat overlooking the mountains — warm textiles, quiet mornings, and light that arrives on its own terms.", specs: ["1 King Bed", "Mountain View", "Ensuite Bath"] },
  { no: "02", name: "Premium Family",       img: IMAGES.roomLuxury,  feature: "For families of 3–4",  rate: 3653, desc: "Spacious room with a private view, thoughtfully arranged for parents and children alike.",                                specs: ["Twin + Queen", "Sitting Nook", "Mountain View"] },
  { no: "03", name: "Premium Family Suite", img: IMAGES.cottage,     feature: "For larger families",  rate: 4059, desc: "A suite that carries the calm of the Himalayas indoors — space, light, and warmth in abundance.",                          specs: ["Two Bedrooms", "Living Area", "Panoramic View"] },
];

const amenities = [
  { icon: Wifi, t: "High-speed Wi-Fi" },
  { icon: Utensils, t: "Sattvic Kitchen" },
  { icon: Mountain, t: "Mountain Views" },
  { icon: ShieldCheck, t: "24×7 Concierge" },
  { icon: Wind, t: "Fresh Alpine Air" },
  { icon: Coffee, t: "Morning Chai" },
];

export default function Stay() {
  const [params] = useSearchParams();
  const initial = {
    check_in:  params.get("ci") || "",
    check_out: params.get("co") || "",
    guests:    params.get("g")  || "2",
  };

  useEffect(() => {
    if (window.location.hash === "#enquire") {
      setTimeout(() => {
        document.getElementById("enquire")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, []);

  return (
    <div data-testid="page-stay">
      <section className="relative h-[86vh] min-h-[560px] overflow-hidden">
        <img src={IMAGES.roomLuxury} alt="Kedarnath Cottage boutique room" className="absolute inset-0 w-full h-full object-cover kc-hero-image" />
        <div className="absolute inset-0 hero-scrim-strong" />
        <div className="relative z-10 h-full max-w-[1500px] mx-auto px-6 md:px-12 flex flex-col justify-end pb-24 text-[#F8F6F2]">
          <div className="overline text-[#D4AF37] kc-fade-up">The Stay · Sersi, Rudraprayag</div>
          <h1 className="h-display text-[44px] sm:text-[56px] md:text-[72px] lg:text-[88px] font-light leading-[1.02] mt-6 max-w-4xl kc-fade-up kc-delay-1 tracking-[-0.025em]" style={{textShadow:"0 2px 32px rgba(0,0,0,0.35)"}}>
            Stay close to <span className="italic text-[#D4AF37]">nature</span>. Feel close to Mahadev.
          </h1>
          <p className="mt-8 text-[#F8F6F2]/80 max-w-xl text-[15px] leading-relaxed kc-fade-up kc-delay-2">Kedarnath Cottage — moments from the Heritage Aviation Helipad. Hours from the divine.</p>
          <div className="mt-10 flex flex-wrap gap-3 kc-fade-up kc-delay-3">
            <a href="#enquire" className="btn-gold" data-testid="stay-cta-book">Book Your Stay <ArrowUpRight size={14} /></a>
            <a href="#enquire" className="btn-ghost-light" data-testid="stay-cta-reserve">Reserve Your Stay</a>
          </div>
        </div>
      </section>

      <TrustBand />

      <section className="section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end mb-16">
              <div className="md:col-span-6">
                <div className="overline">The Rooms</div>
                <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.05] tracking-[-0.02em]">Three ways to arrive at rest.</h2>
              </div>
              <p className="md:col-span-5 md:col-start-8 text-ink-soft text-[14.5px] leading-relaxed">Every room at Kedarnath Cottage is built around the same idea — silence, warmth, and a view that reminds you why you came.</p>
            </div>
          </Reveal>

          <div className="space-y-16 md:space-y-24">
            {rooms.map((r, i) => (
              <Reveal key={r.name} className={`grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-16 items-center ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
                <div className="md:col-span-7 img-zoom h-[360px] md:h-[520px]" data-testid={`room-card-${i}`}>
                  <img src={r.img} alt={r.name} className="w-full h-full object-cover" loading="lazy" />
                </div>
                <div className="md:col-span-5">
                  <div className="index-num">№ {r.no}</div>
                  <div className="text-[10px] uppercase tracking-[0.32em] text-gold mt-2">{r.feature}</div>
                  <h3 className="font-serif-display text-3xl md:text-4xl mt-6 font-light leading-[1.05] tracking-[-0.02em]">{r.name}</h3>
                  <div className="mt-4 flex items-baseline gap-2" data-testid={`room-rate-${i}`}>
                    <span className="text-[10px] uppercase tracking-[0.32em] text-ink-mute">From</span>
                    <span className="font-serif-display text-3xl text-ink">₹{r.rate.toLocaleString("en-IN")}</span>
                    <span className="text-[10px] uppercase tracking-[0.32em] text-ink-mute">/ night</span>
                  </div>
                  <p className="text-ink-soft mt-6 text-[14.5px] leading-relaxed">{r.desc}</p>
                  <ul className="mt-8 space-y-3 text-[13px] uppercase tracking-[0.24em] text-ink-soft">
                    {r.specs.map(s => <li key={s} className="flex items-center gap-3"><span className="w-6 h-px bg-gold" />{s}</li>)}
                  </ul>
                  <a href="#enquire" className="link-underline mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.32em] text-ink">Enquire <ArrowUpRight size={14} /></a>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-cream section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12">
          <Reveal>
            <div className="grid grid-cols-12 gap-10 mb-16">
              <div className="col-span-12 md:col-span-6">
                <div className="overline">Amenities</div>
                <h2 className="font-serif-display text-4xl md:text-5xl mt-6 font-light leading-[1.05] tracking-[-0.02em]">Quiet comforts, thoughtfully placed.</h2>
              </div>
            </div>
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-px bg-ink/10">
            {amenities.map((a, i) => (
              <Reveal delay={i * 80} key={a.t} className="bg-cream p-8 flex flex-col items-start gap-5">
                <div className="index-num">№ 0{i + 1}</div>
                <a.icon size={22} className="text-gold" strokeWidth={1.2} />
                <div className="text-[13px] uppercase tracking-[0.22em] text-ink">{a.t}</div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="enquire" className="section-pad">
        <div className="max-w-[1500px] mx-auto px-6 md:px-12 grid grid-cols-1 md:grid-cols-12 gap-12">
          <Reveal className="md:col-span-5">
            <div className="overline">Nearby</div>
            <h2 className="font-serif-display text-3xl md:text-4xl mt-6 font-light leading-[1.1] tracking-[-0.02em]">Sersi puts you close to it all.</h2>
            <ul className="mt-12 divide-y divide-[color:var(--kc-line)]">
              {[
                ["Heritage Aviation Helipad", "Walking distance"],
                ["Sonprayag · Gaurikund",     "Kedarnath yatra base"],
                ["Guptkashi",                 "Ancient temples & viewpoints"],
                ["Rudraprayag Sangam",        "Sacred river confluence"],
              ].map(([n, d]) => (
                <li key={n} className="py-5 flex justify-between items-center">
                  <span className="text-ink text-[15px]">{n}</span>
                  <span className="text-ink-mute text-[11px] uppercase tracking-[0.24em]">{d}</span>
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={150} className="md:col-span-7"><EnquiryForm type="stay" initial={initial} /></Reveal>
        </div>
      </section>
    </div>
  );
}
